import { createContext, useContext, useEffect, useState } from "react";
import { toast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface CartItem {
    uniqueId: string;
    productId: string;
    variantId?: string;
    name: string;
    price: number;
    image: string;
    quantity: number;
    maxStock: number;
}

export interface GlobalDiscount {
    enabled: boolean;
    title: string;
    percentage: number;
    start_date: string | null;
    end_date: string | null;
    show_counter: boolean;
}

interface CartContextType {
    items: CartItem[];
    addToCart: (item: Omit<CartItem, "uniqueId">) => void;
    removeFromCart: (uniqueId: string) => void;
    updateQuantity: (uniqueId: string, quantity: number) => void;
    clearCart: () => void;
    cartCount: number;
    cartTotal: number;
    openCart: boolean;
    setOpenCart: (open: boolean) => void;
    globalDiscount: GlobalDiscount | null;
    applyDiscount: (price: number) => number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
    const [items, setItems] = useState<CartItem[]>([]);
    const [openCart, setOpenCart] = useState(false);

    // Load from local storage
    useEffect(() => {
        const savedCart = localStorage.getItem("cart");
        if (savedCart) {
            try {
                setItems(JSON.parse(savedCart));
            } catch (e) {
                console.error("Failed to parse cart", e);
            }
        }
    }, []);

    // Save to local storage
    useEffect(() => {
        localStorage.setItem("cart", JSON.stringify(items));
    }, [items]);

    // Fetch Global Discount
    const { data: globalDiscount } = useQuery({
        queryKey: ["global-discount"],
        queryFn: async () => {
            const { data, error } = await supabase
                .from("settings")
                .select("value")
                .eq("key", "discount")
                .single();
            if (error) return null;
            return data.value as GlobalDiscount;
        }
    });

    const applyDiscount = (price: number) => {
        if (!globalDiscount || !globalDiscount.enabled || !globalDiscount.percentage) return price;

        const now = new Date();
        if (globalDiscount.start_date && new Date(globalDiscount.start_date) > now) return price;
        if (globalDiscount.end_date && new Date(globalDiscount.end_date) < now) return price;

        return price * (1 - globalDiscount.percentage / 100);
    };

    const addToCart = (newItem: Omit<CartItem, "uniqueId">) => {
        setItems((prev) => {
            const uniqueId = newItem.variantId
                ? `${newItem.productId}-${newItem.variantId}`
                : newItem.productId;

            const existing = prev.find((item) => item.uniqueId === uniqueId);

            if (existing) {
                const newQuantity = Math.min(existing.quantity + newItem.quantity, newItem.maxStock);

                if (newQuantity === existing.quantity) {
                    toast({
                        title: "Max stock reached",
                        description: "You have reached the maximum available stock for this item.",
                        variant: "destructive",
                    });
                    return prev;
                }

                toast({
                    title: "Cart Updated",
                    description: `Increased quantity of ${newItem.name}`,
                });

                return prev.map((item) =>
                    item.uniqueId === uniqueId
                        ? { ...item, quantity: newQuantity }
                        : item
                );
            }

            toast({
                title: "Added to Cart",
                description: `${newItem.name} added to your cart`,
            });

            return [...prev, { ...newItem, uniqueId }];
        });
    };

    const removeFromCart = (uniqueId: string) => {
        setItems((prev) => prev.filter((item) => item.uniqueId !== uniqueId));
    };

    const updateQuantity = (uniqueId: string, quantity: number) => {
        setItems((prev) =>
            prev.map((item) => {
                if (item.uniqueId === uniqueId) {
                    const newQty = Math.max(1, Math.min(quantity, item.maxStock));
                    return { ...item, quantity: newQty };
                }
                return item;
            })
        );
    };

    const clearCart = () => {
        setItems([]);
        localStorage.removeItem("cart");
    };

    const cartCount = items.reduce((acc, item) => acc + item.quantity, 0);
    // Use applyDiscount for total calculation
    const cartTotal = items.reduce((acc, item) => acc + (applyDiscount(item.price) * item.quantity), 0);

    return (
        <CartContext.Provider
            value={{
                items,
                addToCart,
                removeFromCart,
                updateQuantity,
                clearCart,
                cartCount,
                cartTotal,
                openCart,
                setOpenCart,
                globalDiscount: globalDiscount || null,
                applyDiscount
            }}
        >
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
    const context = useContext(CartContext);
    if (context === undefined) {
        throw new Error("useCart must be used within a CartProvider");
    }
    return context;
};
